import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { RequireWizard } from './components/onboarding/RequireWizard';
import { WorkspaceNotReady } from './components/dashboard/WorkspaceNotReady';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './store/useAuthStore';
import { Toaster } from './components/ui/sonner';
import { usePermission } from './hooks/usePermission';
import type { Permission } from './config/rolePermissions';
import './i18n';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ClarityRouteObserver, ClaritySession, trackClarityEvent } from './lib/observability';

// Pages
// Login is kept eagerly imported: it is the entry route for unauthenticated
// users, so loading it synchronously avoids a spinner flash on first paint.
import { Login } from './pages/Login';
import { Impersonate } from './pages/Impersonate';
// Landing is the public entry route (/) for new visitors. Eagerly imported (like Login)
// so the first paint shows the page directly instead of the Suspense "Loading module…"
// spinner while a separate lazy chunk downloads.
import { Landing } from './pages/Landing';

// Layouts
import { DashboardLayout } from './layouts/DashboardLayout';
import { ModuleGuard } from './components/auth/ModuleGuard';

import { useThemeStore } from './store/useThemeStore';

import { lazy, Suspense, useEffect, useRef, useState } from 'react';

// Public marketing pages — lazy-loaded to keep initial bundle small
const Pricing = lazy(() => import('./pages/Pricing').then((m) => ({ default: m.Pricing })));
const Signup = lazy(() => import('./pages/Signup').then((m) => ({ default: m.Signup })));
const Verify = lazy(() => import('./pages/Verify').then((m) => ({ default: m.Verify })));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Security = lazy(() => import('./pages/Security'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Resources = lazy(() => import('./pages/Resources'));
const ResourceGuidePage = lazy(() => import('./pages/ResourceGuidePage'));
const DesignSystem = lazy(() => import('./pages/DesignSystem'));

// Authenticated pages — lazy-loaded so each route ships as its own chunk
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Reports = lazy(() => import('./pages/Reports').then((m) => ({ default: m.Reports })));
const FinancialDashboard = lazy(() => import('./pages/FinancialDashboard').then((m) => ({ default: m.FinancialDashboard })));
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const ChangePassword = lazy(() => import('./pages/ChangePassword').then((m) => ({ default: m.ChangePassword })));
const Khata = lazy(() => import('./pages/Khata').then((m) => ({ default: m.Khata })));
const Invoices = lazy(() => import('./pages/Invoices').then((m) => ({ default: m.Invoices })));
const Contacts = lazy(() => import('./pages/Contacts').then((m) => ({ default: m.Contacts })));
const AgingReports = lazy(() => import('./pages/AgingReports').then((m) => ({ default: m.AgingReports })));
const Inventory = lazy(() => import('./pages/Inventory'));
const AdvancedProductFormWrapper = lazy(() => import('./pages/Inventory').then(m => ({ default: m.AdvancedProductFormWrapper })));
const DamagedGoods = lazy(() => import('./pages/DamagedGoods'));
const Purchase = lazy(() => import('./pages/Purchase'));
const POS = lazy(() => import('./pages/POS'));
const POSTerminal = lazy(() => import('./pages/POSTerminal'));
const HR = lazy(() => import('./pages/HR'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Timesheets = lazy(() => import('./pages/Timesheets'));
const Expenses = lazy(() => import('./pages/Expenses'));
const CashBook = lazy(() => import('./pages/CashBook'));
const DemandForecast = lazy(() => import('./pages/DemandForecast'));
const TeamManagement = lazy(() => import('./pages/TeamManagement'));
const Partners = lazy(() => import('./pages/Partners'));
const VatReport = lazy(() => import('./pages/VatReport'));
const Accounts = lazy(() => import('./pages/Accounts'));
const JournalEntries = lazy(() => import('./pages/JournalEntries'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const MyAttendance = lazy(() => import('./pages/MyAttendance'));
const MyLeaves = lazy(() => import('./pages/MyLeaves'));
const HRAssistance = lazy(() => import('./pages/HRAssistance'));
const MyPayslips = lazy(() => import('./pages/MyPayslips'));
const BusinessProfile = lazy(() => import('./pages/BusinessProfile'));
const Billing = lazy(() => import('./pages/Billing').then(m => ({ default: m.Billing })));
const Organization = lazy(() => import('./pages/Organization').then(m => ({ default: m.Organization })));
const ApprovalInbox = lazy(() => import('./pages/ApprovalInbox').then(m => ({ default: m.ApprovalInbox })));
const ApprovalAnalytics = lazy(() => import('./pages/ApprovalAnalytics').then(m => ({ default: m.ApprovalAnalytics })));
const ApprovalDashboard = lazy(() => import('./pages/ApprovalDashboard').then(m => ({ default: m.ApprovalDashboard })));
const ApprovalErrorBoundary = lazy(() => import('./components/ApprovalErrorBoundary').then(m => ({ default: m.ApprovalErrorBoundary })));
const GrocerySetupWizard = lazy(() => import('./components/onboarding/GrocerySetupWizard').then(m => ({ default: m.GrocerySetupWizard })));
const ExpiryPage = lazy(() => import('./pages/ExpiryPage'));
const QuickSale = lazy(() => import('./pages/QuickSale'));
const QuickAddProduct = lazy(() => import('./pages/QuickAddProduct'));
const QuickReceiveStock = lazy(() => import('./pages/QuickReceiveStock'));
const CloseDay = lazy(() => import('./pages/CloseDay'));
const BulkImportProduct = lazy(() => import('./pages/BulkImportProduct'));
const WasteReport = lazy(() => import('./pages/WasteReport'));
const Checklist = lazy(() => import('./pages/Checklist'));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 1,
      retryDelay: 2000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

const RouteFallback = () => (
  <div className="flex h-[60vh] w-full items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
      <p className="text-xs font-medium text-muted-foreground">Loading module...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ permission }: { permission?: Permission }) => {
  const { isAuthenticated, hasHydrated, isBootstrapped, bootstrapError, business, user } = useAuthStore();
  const { hasPermission } = usePermission();
  const location = useLocation();

  // Observe gate hits for Clarity (no-op when disabled). Effect-based so it fires once per
  // block, not on every re-render, and never during render.
  const ready = hasHydrated && (!isAuthenticated || isBootstrapped);
  const roleBlocked = ready && isAuthenticated && !!user && !!permission && !hasPermission(permission);
  const firstLoginBlocked =
    ready && isAuthenticated && !!user?.isFirstLogin && location.pathname !== '/change-password';
  useEffect(() => {
    if (roleBlocked) {
      trackClarityEvent('role-gate-hit', { gate_permission: permission });
      trackClarityEvent('protected-route-blocked', { gate_route: location.pathname });
    }
  }, [roleBlocked, permission, location.pathname]);
  useEffect(() => {
    if (firstLoginBlocked) trackClarityEvent('auth-first-login-password-required');
  }, [firstLoginBlocked]);

  // Wait for bootstrap to finish for any authed session. The access token is NOT required
  // here: a reloaded normal session has no in-memory token (it lives in the HttpOnly cookie)
  // and is validated by bootstrap (200 -> stays authed, 401 -> bootstrap logs out).
  if (!hasHydrated || (isAuthenticated && !isBootstrapped)) {
    return null;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // First login: mandatory password change. Enforce a single, path-independent policy
  // (Login also redirects here) so the temporary password can't be kept by dismissing a
  // soft banner. ChangePassword clears isFirstLogin on success; Sign Out is the escape.
  if (user?.isFirstLogin && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Provisioning gate: an account not yet linked to a tenant (bootstrap cleanly reported no
  // business) has no workspace to load. Divert to /pending BEFORE the dashboard/wizard so it
  // never mounts data-gated UI it can't use. Guarded on !bootstrapError so a degraded bootstrap
  // (directory/network outage) never strands a real tenant here — the dashboard's
  // WorkspaceNotReady backstop covers that case. /change-password stays reachable so a
  // tenant-less first-login user can still set a password before landing on /pending.
  if (isBootstrapped && !bootstrapError) {
    const hasTenant = Boolean(business?.tenantId);
    const pendingExempt = location.pathname === '/pending' || location.pathname === '/change-password';
    if (!hasTenant && !pendingExempt) {
      return <Navigate to="/pending" replace />;
    }
    if (hasTenant && location.pathname === '/pending') {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, hasHydrated, isBootstrapped, user } = useAuthStore();
  if (!hasHydrated) return null;
  if (isAuthenticated && !isBootstrapped) return null;
  if (!isAuthenticated || !user) return <>{children}</>;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  const { hasHydrated, isAuthenticated, isBootstrapped, logout, initAuth } = useAuthStore();

  // Permission store loaded check - kept but not blocking bootstrap
  const bootstrapRef = useRef(false);
  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);
  const [bootstrapStage, setBootstrapStage] = useState(0);
  const { theme } = useThemeStore();

  const safeRedirectToLogin = () => {
    const win = window as Window & { __resetTriggered?: boolean };
    if (window.location.pathname === '/login') return;
    if (win.__resetTriggered) return;
    win.__resetTriggered = true;
    window.location.replace('/login');
  };

  useEffect(() => {
    type ResetWindow = Window & {
      resetApp?: () => void;
      forceReset?: () => void;
    };

    const win = window as ResetWindow;

    const resetImpl = () => {
      try {
        logout();
      } catch {
        // Continue with storage clear fallback.
      }

      localStorage.clear();
      sessionStorage.clear();

        safeRedirectToLogin();

      // Ensure hard navigation even if SPA/router state is broken.
      setTimeout(() => {
        if (!window.location.pathname.includes('/login')) {
          safeRedirectToLogin();
        }
      }, 50);
    };

    win.resetApp = () => resetImpl();
    win.forceReset = () => resetImpl();

    console.log('[App] Global reset handlers registered:', {
      resetApp: typeof win.resetApp,
      forceReset: typeof win.forceReset,
    });
  }, [logout]);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
    }
  }, [theme]);

  useEffect(() => {
    // Run bootstrap for any authed session. We intentionally do NOT require an in-memory
    // access token: a reloaded normal session has none (the token lives in the HttpOnly
    // cookie) and bootstrap revalidates it via that cookie.
    if (!hasHydrated || !isAuthenticated || isBootstrapped || bootstrapRef.current) return;

    bootstrapRef.current = true;

    initAuth().then(() => {
      setBootstrapTimedOut(false);
    })
      .catch(() => {
        useAuthStore.setState({ isBootstrapped: true });
      })
      .finally(() => {
        bootstrapRef.current = false;
      });
  }, [hasHydrated, isAuthenticated, isBootstrapped, initAuth]);



  // Advance the bootstrap loading message through stages so a multi-second wait reads
  // as progress, not a hang; the 8s stage also surfaces the safe-reset hint.
  useEffect(() => {
    const bootstrapping = isAuthenticated && !isBootstrapped;
    if (!bootstrapping) return undefined;
    const t1 = setTimeout(() => setBootstrapStage(1), 1500);
    const t2 = setTimeout(() => {
      setBootstrapStage(2);
      setBootstrapTimedOut(true);
    }, 8000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isAuthenticated, isBootstrapped]);

  if (!hasHydrated) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Loading BizCore...</p>
        </div>
      </div>
    );
  }

  // Show the bootstrap loading screen for ANY authed-but-not-bootstrapped session — do NOT
  // gate on `user`. On reload the persisted session has isAuthenticated=true but user=null
  // until /bootstrap returns; gating on `user` let it fall through to the router, where
  // ProtectedRoute returns null → a blank screen for the whole (cold-start) bootstrap window.
  if (isAuthenticated && !isBootstrapped) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">
            {['Preparing your workspace…', 'Loading your data…', 'Almost there…'][bootstrapStage]}
          </p>
          {bootstrapTimedOut && (
            <p className="text-xs text-amber-600">Still preparing workspace. You can safely reset below.</p>
          )}
          <button
            onClick={() => {
              const win = window as Window & { forceReset?: () => void };
              if (typeof win.forceReset === 'function') {
                win.forceReset();
                return;
              }
              localStorage.clear();
              sessionStorage.clear();
              safeRedirectToLogin();
            }}
            className="text-xs text-primary underline mt-2 block mx-auto"
          >
            Taking too long? Click here
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ClaritySession />
        <Toaster position="bottom-right" />
        <Router>
          <ClarityRouteObserver />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
            {/* Informational page (like /about, /contact) — viewable by everyone, incl. authenticated
                users reaching it from in-app upgrade CTAs (TrialBanner, LockedModule, BusinessProfile).
                Must NOT be PublicRoute-gated or logged-in users bounce to /dashboard. */}
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
            <Route path="/verify" element={<PublicRoute><Verify /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/impersonate" element={<Impersonate />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/security" element={<Security />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/resources/:slug" element={<ResourceGuidePage />} />
            <Route path="/design-system" element={<DesignSystem />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/setup" element={<GrocerySetupWizard />} />
              <Route path="/pending" element={<WorkspaceNotReady />} />
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<RequireWizard />}>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />

                <Route element={<ProtectedRoute permission="khata.view" />}>
                  <Route path="/khata" element={<ModuleGuard moduleCode="khata"><Khata /></ModuleGuard>} />
                </Route>

                <Route element={<ProtectedRoute permission="invoices.view" />}>
                  <Route path="/contacts" element={<ModuleGuard moduleCode="invoices"><Contacts /></ModuleGuard>} />
                  <Route path="/invoices" element={<ModuleGuard moduleCode="invoices"><Invoices /></ModuleGuard>} />
                  <Route path="/partners" element={<ModuleGuard moduleCode="invoices"><Partners /></ModuleGuard>} />
                </Route>

                <Route element={<ProtectedRoute permission="reports.view" />}>
                  <Route path="/reports" element={<ModuleGuard moduleCode="reports"><Reports /></ModuleGuard>} />
                  <Route path="/financial-dashboard" element={<ModuleGuard moduleCode="reports"><FinancialDashboard /></ModuleGuard>} />
                  <Route path="/reports/aging" element={<ModuleGuard moduleCode="reports"><AgingReports /></ModuleGuard>} />
                  <Route path="/cashbook" element={<ModuleGuard moduleCode="reports"><CashBook /></ModuleGuard>} />
                  <Route path="/vat-report" element={<ModuleGuard moduleCode="reports"><VatReport /></ModuleGuard>} />
                  <Route path="/accounts" element={<ModuleGuard moduleCode="reports"><Accounts /></ModuleGuard>} />
                  <Route path="/journal-entries" element={<ModuleGuard moduleCode="reports"><JournalEntries /></ModuleGuard>} />
                  <Route path="/analytics" element={<ModuleGuard moduleCode="reports"><AnalyticsDashboard /></ModuleGuard>} />
                </Route>

                <Route element={<ProtectedRoute permission="inventory.view" />}>
                  <Route path="/inventory" element={<ModuleGuard moduleCode="inventory"><Inventory /></ModuleGuard>} />
                  <Route path="/damaged-goods" element={<ModuleGuard moduleCode="inventory"><DamagedGoods /></ModuleGuard>} />
                  <Route path="/demand-forecast" element={<ModuleGuard moduleCode="inventory"><DemandForecast /></ModuleGuard>} />
                  <Route path="/expiry" element={<ModuleGuard moduleCode="inventory"><ExpiryPage /></ModuleGuard>} />
                  <Route path="/waste" element={<ModuleGuard moduleCode="inventory"><WasteReport /></ModuleGuard>} />
                  <Route path="/checklists" element={<ModuleGuard moduleCode="inventory"><Checklist /></ModuleGuard>} />
                  <Route path="/inventory/quick-add" element={<ModuleGuard moduleCode="inventory"><QuickAddProduct /></ModuleGuard>} />
                  <Route path="/inventory/quick-receive" element={<ModuleGuard moduleCode="inventory"><QuickReceiveStock /></ModuleGuard>} />
                  <Route path="/inventory/bulk-import" element={<ModuleGuard moduleCode="inventory"><BulkImportProduct /></ModuleGuard>} />
                  <Route path="/inventory/advanced" element={<ModuleGuard moduleCode="inventory"><AdvancedProductFormWrapper /></ModuleGuard>} />
                </Route>

                <Route element={<ProtectedRoute permission="purchase.view" />}>
                  <Route path="/purchase" element={<ModuleGuard moduleCode="purchase"><Purchase /></ModuleGuard>} />
                </Route>

                <Route element={<ProtectedRoute permission="pos.access" />}>
                  <Route path="/pos" element={<ModuleGuard moduleCode="pos"><POS /></ModuleGuard>} />
                </Route>

                <Route element={<ProtectedRoute permission="hr.view" />}>
                  <Route path="/hr" element={<ModuleGuard moduleCode="hr"><HR /></ModuleGuard>} />
                </Route>

                <Route path="/hr/my-attendance" element={<MyAttendance />} />
                <Route path="/hr/my-leaves" element={<MyLeaves />} />
                <Route path="/hr/my-payslips" element={<MyPayslips />} />
                <Route path="/hr/assistance" element={<HRAssistance />} />

                <Route element={<ProtectedRoute permission="projects.view" />}>
                  <Route path="/projects" element={<ModuleGuard moduleCode="projects"><Projects /></ModuleGuard>} />
                  <Route path="/projects/:id" element={<ModuleGuard moduleCode="projects"><ProjectDetail /></ModuleGuard>} />
                  <Route path="/timesheets" element={<ModuleGuard moduleCode="projects"><Timesheets /></ModuleGuard>} />
                </Route>

                <Route element={<ProtectedRoute permission="expenses.view" />}>
                  <Route path="/expenses" element={<ModuleGuard moduleCode="expenses"><Expenses /></ModuleGuard>} />
                </Route>

                <Route element={<ProtectedRoute permission="team.manage" />}>
                  <Route path="/team" element={<TeamManagement />} />
                </Route>
                <Route element={<ProtectedRoute permission="settings.view" />}>
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/organization" element={<Organization />} />
                  <Route path="/billing" element={<Billing />} />
                </Route>
                <Route element={<ProtectedRoute permission="settings.view" />}>
                  <Route path="/approval-dashboard" element={<ApprovalErrorBoundary><ApprovalDashboard /></ApprovalErrorBoundary>} />
                  <Route path="/approval-inbox" element={<ApprovalErrorBoundary><ApprovalInbox /></ApprovalErrorBoundary>} />
                  <Route path="/approval-analytics" element={<ApprovalErrorBoundary><ApprovalAnalytics /></ApprovalErrorBoundary>} />
                </Route>
                <Route path="/business-profile" element={<BusinessProfile />} />
              </Route>
              </Route>

              <Route element={<ProtectedRoute permission="pos.access" />}>
                <Route path="/pos/terminal" element={<ModuleGuard moduleCode="pos"><POSTerminal /></ModuleGuard>} />
                <Route path="/pos/quick" element={<ModuleGuard moduleCode="pos"><QuickSale /></ModuleGuard>} />
                <Route path="/pos/close-day" element={<ModuleGuard moduleCode="pos"><CloseDay /></ModuleGuard>} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
